/* eslint-disable i18next/no-literal-string */

import { Box, Icon, Theme, Typography } from "@outschool/backpack";
import type { IconDefinition } from "@outschool/icons";
import React from "react";

export function ChipButton({
  label,
  selected,
  onClick,
  icon,
  description,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
  icon?: IconDefinition | string;
  description?: string;
}) {
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      sx={(theme: Theme) => ({
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: 0.25,
        border: `1px solid ${
          selected ? theme.palette.primary.main : theme.palette.grey[300]
        }`,
        borderRadius: "4px",
        padding: description ? "6px 10px" : "10px 6px",
        cursor: "pointer",
        backgroundColor: selected ? theme.palette.primary.main : "transparent",
        color: selected
          ? theme.palette.common.white
          : theme.palette.text.primary,
        transition: "all 0.15s ease",
        minWidth: 0,
        maxWidth: "100%",
        boxSizing: "border-box",
        "&:hover": {
          borderColor: theme.palette.primary.main,
          backgroundColor: selected
            ? theme.palette.primary.dark
            : theme.palette.primary[50] || "rgba(103, 58, 183, 0.04)",
        },
      })}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          width: "100%",
          minWidth: 0,
        }}
      >
        {icon && typeof icon === "object" ? (
          <Icon
            icon={icon}
            sx={{
              fontSize: "1.7rem",
              flexShrink: 0,
              marginRight: { xs: "0", md: "6px" },
            }}
          />
        ) : icon && typeof icon === "string" ? (
          <Box
            src={icon}
            alt={label}
            component="img"
            sx={{
              fontSize: "1.7rem",
              flexShrink: 0,
              marginRight: { xs: "0", md: "6px" },
            }}
          />
        ) : null}
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            textAlign: "center",
            overflowWrap: "break-word",
            wordBreak: "break-word",
            minWidth: 0,
          }}
        >
          {label}
        </Typography>
      </Box>
      {description && (
        <Typography
          variant="caption"
          sx={{
            opacity: 0.75,
            lineHeight: 1.3,
            fontSize: "1rem",
            textAlign: "center",
          }}
        >
          {description}
        </Typography>
      )}
    </Box>
  );
}

export function ColorSwatch({
  color,
  selected,
  onClick,
}: {
  color: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label={color}
      onClick={onClick}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      sx={(theme: Theme) => ({
        boxSizing: "border-box",
        width: 32,
        height: 32,
        flexShrink: 0,
        borderRadius: "50%",
        backgroundColor: color,
        cursor: "pointer",
        border: selected
          ? `2.5px solid ${theme.palette.primary.main}`
          : `2px solid ${theme.palette.grey[300]}`,
        outline: selected ? `2px solid ${theme.palette.primary.main}` : "none",
        outlineOffset: selected ? "2px" : 0,
        transition: "all 0.15s ease",
        "&:hover": {
          transform: "scale(1.15)",
          borderColor: theme.palette.primary.main,
        },
      })}
    />
  );
}
